<template>
  <div>
    <!-- Sin certificado configurado -->
    <div v-if="!hasCertificate">
      <div v-if="ambit === ambits.profile">
        <h3>Firma Simple - Proveedor EDigital</h3>
        <p>
          Para comenzar a operar con <b>Firma Simple</b>, primero debemos crearte un
          <b>Certificado Digital</b> con el proveedor EDigital.
          Es como tu firma en papel, pero ahora será electrónica y mucho más segura.
        </p>
        <p>
          Comunícate con tu administrador de sistema para que te habilite tu certificado.
        </p>
      </div>
      <div v-else>
        <ActiveSignEDigital
          :configurations="requestData"
          :user="user"
          @activate="activate"
        />
      </div>
    </div>

    <!-- Con certificado configurado -->
    <div v-if="hasCertificate">
      <div class="row justify-content-center mb-3">
        <div class="col-lg-8 col-md-12">
          <div class="card">
            <div class="card-body">
              <h4 class="card-title">Certificado EDigital</h4>
              <div class="row">
                <div class="col-md-6">
                  <p><strong>Nombre:</strong> {{ formData.cedulaNombre }} {{ formData.cedulaApellidos }}</p>
                  <p><strong>Correo:</strong> {{ formData.correoPersonal }}</p>
                  <p><strong>Tipo documento:</strong> {{ documentTypeLabel }}</p>
                  <p><strong>Número de serie:</strong> {{ formData.identityDocument }}</p>
                </div>
                <div class="col-md-6">
                  <p><strong>Género:</strong> {{ genderLabel }}</p>
                  <p><strong>Fecha nacimiento:</strong> {{ formData.cedulaFnacimiento }}</p>
                  <p>
                    <strong>Estado:</strong>
                    <span :class="formData.activa ? 'text-success' : 'text-danger'">
                      {{ formData.activa ? 'Activa' : 'Inactiva' }}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Formulario de contraseña (solo perfil) -->
      <div v-if="ambit === ambits.profile">
        <div class="row justify-content-center">
          <div class="col-lg-6 col-md-8">
            <div class="form-group">
              <label for="hashPassword">Contraseña del certificado</label>
              <input
                type="password"
                class="form-control"
                id="hashPassword"
                name="hashPassword"
                v-model="formData.hash"
                placeholder="Ingrese contraseña del certificado"
                autocomplete="off"
                v-validate="'required|min:6'"
              />
              <small class="form-text text-muted" v-if="formData.existeEdigital">
                Ingrese la misma contraseña que usa en EDigital.
              </small>
              <small class="form-text text-danger" v-if="errors.has('hashPassword')">
                {{ errors.first('hashPassword') }}
              </small>
            </div>
          </div>
        </div>
        <div class="row justify-content-center mt-3">
          <div class="col-lg-6 col-md-8 text-right">
            <button
              class="btn btn-primary"
              type="button"
              @click="savePassword"
              :disabled="saving"
            >
              <b-spinner small v-if="saving" variant="light" />
              <span v-else>Guardar contraseña</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import ActiveSignEDigital from '@/febos/global/usuario/components/sign/simple/edigital/ActiveSign';
import { configureSimpleAndEsign7Sign } from '@/febos/servicios/api/usuarios.api';
import {
  PROVEEDOR,
  ambits,
  documentTypes,
  genderOptions
} from '@/febos/global/usuario/components/sign/simple/edigital/constants';

export default {
  name: 'SimpleSignEDigital',
  components: {
    ActiveSignEDigital
  },
  data() {
    return {
      ambits,
      saving: false,
      formData: {
        id: null,
        cedulaNombre: '',
        cedulaApellidos: '',
        cedulaFnacimiento: '',
        cedulaGender: '',
        correoPersonal: '',
        identityDocument: '',
        typeIdentityDocument: '',
        cedulaAnteriorUrl: '',
        cedulaPosteriorUrl: '',
        hash: '',
        activa: false,
        existeEdigital: false
      }
    };
  },
  computed: {
    ...mapGetters('Usuario', ['currentUser']),
    ...mapGetters('Empresas', ['company']),
    hasCertificate() {
      return !!this.formData.id;
    },
    documentTypeLabel() {
      const found = documentTypes.find((dt) => dt.id === this.formData.typeIdentityDocument);
      return found ? found.nombre : this.formData.typeIdentityDocument;
    },
    genderLabel() {
      const found = genderOptions.find((g) => g.id === this.formData.cedulaGender);
      return found ? found.nombre : this.formData.cedulaGender;
    }
  },
  mounted() {
    this.loadExistingData();
  },
  methods: {
    loadExistingData() {
      const data = this.requestData;
      if (data && data.respuestaSimple && data.id) {
        this.formData.id = data.id;
        this.formData.activa = data.respuestaSimple.activa;
        this.formData.identityDocument = data.respuestaSimple.identityDocument;
        this.formData.typeIdentityDocument = data.respuestaSimple.typeIdentityDocument;
        this.formData.correoPersonal = data.respuestaSimple.correoPersonal;
        this.formData.cedulaNombre = data.respuestaSimple.cedulaNombre;
        this.formData.cedulaApellidos = data.respuestaSimple.cedulaApellidos;
        this.formData.cedulaFnacimiento = data.respuestaSimple.cedulaFnacimiento;
        this.formData.cedulaGender = data.respuestaSimple.cedulaGender;
        this.formData.cedulaAnteriorUrl = data.respuestaSimple.cedulaAnteriorUrl;
        this.formData.cedulaPosteriorUrl = data.respuestaSimple.cedulaPosteriorUrl;
        this.formData.hash = data.respuestaSimple.hash;
      } else if (data && data.respuestaSimple && data.respuestaSimple.existeEdigital) {
        this.formData.existeEdigital = true;
        this.formData.correoPersonal = data.respuestaSimple.preRequest.correoPersonal;
        this.formData.cedulaNombre = data.respuestaSimple.preRequest.cedulaNombre;
        this.formData.cedulaApellidos = data.respuestaSimple.preRequest.cedulaApellidos;
        this.formData.cedulaGender = data.respuestaSimple.preRequest.cedulaGender;
        this.formData.identityDocument = data.respuestaSimple.preRequest.identityDocument;
        this.formData.typeIdentityDocument = data.respuestaSimple.preRequest.typeIdentityDocument;
      }
    },
    async savePassword() {
      const isValid = await this.$validator.validate('hashPassword');
      if (!isValid) return;

      try {
        this.saving = true;
        const data = {
          categoria: 'perfil',
          codigo: '',
          idUsuarioFirma: this.user.id,
          proveedor: PROVEEDOR,
          tipoFirma: 'firmaSimple',
          parametros: {},
          requestEDigital: {
            id: this.formData.id,
            hash: this.formData.hash,
            activa: this.formData.activa
          },
          requestEconomia: {},
          requestEsign: {},
          requestSegpres: {}
        };
        await configureSimpleAndEsign7Sign(data);
        this.$bvToast.toast(
          'Contraseña actualizada correctamente',
          {
            title: 'Firma Simple EDigital',
            variant: 'primary',
            pauseOnHover: true,
            solid: true,
            noAutoHide: true
          }
        );
        this.$emit('activate', {});
      } catch (error) {
        this.$bvToast.toast(
          'Error al guardar la contraseña',
          {
            title: 'Firma Simple EDigital',
            variant: 'danger',
            pauseOnHover: true,
            solid: true,
            noAutoHide: true
          }
        );
      } finally {
        this.saving = false;
      }
    },
    activate() {
      this.$emit('activate', {});
    }
  },
  props: {
    user: {
      type: Object,
      required: true
    },
    signConfiguration: {
      type: Object,
      required: false,
      default: () => ({})
    },
    requestData: {
      type: Object,
      required: false,
      default: () => ({})
    },
    ambit: {
      type: String,
      required: true,
      default: 'perfil'
    }
  }
};
</script>
